const MONTHS_PER_YEAR = 12;
const DAYS_IN_COMMON_YEAR_MONTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAYS_PER_ERA = 146097;
const DAYS_PER_COMMON_YEAR = 365;
const EPOCH_SHIFT_DAYS = 719468;
const assertInteger = (value, label) => {
    if (!Number.isInteger(value)) {
        throw new RangeError(`${label} must be an integer, received ${String(value)}`);
    }
    return value;
};
export const isLeapYear = (year) => {
    assertInteger(year, 'year');
    const isDivisibleByFour = year % 4 === 0;
    const isCenturyButNotLeap = year % 100 === 0 && year % 400 !== 0;
    return isDivisibleByFour && !isCenturyButNotLeap;
};
export const daysInMonth = (year, month) => {
    assertInteger(month, 'month');
    const daysInCommonYear = DAYS_IN_COMMON_YEAR_MONTHS[month - 1];
    if (daysInCommonYear === undefined) {
        throw new RangeError(`month must be between 1 and 12, received ${String(month)}`);
    }
    const isFebruaryOfLeapYear = month === 2 && isLeapYear(year);
    return isFebruaryOfLeapYear ? 29 : daysInCommonYear;
};
export const clampDay = (date) => {
    const lastDayOfMonth = daysInMonth(date.year, date.month);
    assertInteger(date.day, 'day');
    if (date.day < 1) {
        throw new RangeError(`day must be 1 or greater, received ${String(date.day)}`);
    }
    return {
        year: date.year,
        month: date.month,
        day: date.day <= lastDayOfMonth ? date.day : lastDayOfMonth,
    };
};
/**
 * Shifts year and month while keeping `day` untouched, so the result may name a day the
 * target month does not have (31 February). That is deliberate: recurrence anchors must
 * survive short months, and `clampDay` turns the anchor into a real date at the very end.
 * Clamping here instead would make every quarterly series drift down after one February.
 */
export const addMonths = (date, months) => {
    assertInteger(date.year, 'year');
    assertInteger(date.month, 'month');
    assertInteger(months, 'months');
    const absoluteMonth = date.year * MONTHS_PER_YEAR + (date.month - 1) + months;
    const normalizedMonth = ((absoluteMonth % MONTHS_PER_YEAR) + MONTHS_PER_YEAR) % MONTHS_PER_YEAR;
    return {
        year: Math.floor(absoluteMonth / MONTHS_PER_YEAR),
        month: normalizedMonth + 1,
        day: date.day,
    };
};
export const addYears = (date, years) => addMonths(date, assertInteger(years, 'years') * MONTHS_PER_YEAR);
const toEpochDay = (date) => {
    assertInteger(date.day, 'day');
    daysInMonth(date.year, date.month);
    const shiftedYear = date.month <= 2 ? date.year - 1 : date.year;
    const era = Math.floor(shiftedYear / 400);
    const yearOfEra = shiftedYear - era * 400;
    const shiftedMonth = date.month + (date.month > 2 ? -3 : 9);
    const dayOfYear = Math.floor((153 * shiftedMonth + 2) / 5) + date.day - 1;
    const dayOfEra = yearOfEra * DAYS_PER_COMMON_YEAR +
        Math.floor(yearOfEra / 4) -
        Math.floor(yearOfEra / 100) +
        dayOfYear;
    return era * DAYS_PER_ERA + dayOfEra - EPOCH_SHIFT_DAYS;
};
const fromEpochDay = (epochDay) => {
    const shifted = epochDay + EPOCH_SHIFT_DAYS;
    const era = Math.floor(shifted / DAYS_PER_ERA);
    const dayOfEra = shifted - era * DAYS_PER_ERA;
    const yearOfEra = Math.floor((dayOfEra -
        Math.floor(dayOfEra / 1460) +
        Math.floor(dayOfEra / 36524) -
        Math.floor(dayOfEra / 146096)) /
        DAYS_PER_COMMON_YEAR);
    const shiftedYear = yearOfEra + era * 400;
    const dayOfYear = dayOfEra -
        (DAYS_PER_COMMON_YEAR * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
    const shiftedMonth = Math.floor((5 * dayOfYear + 2) / 153);
    const day = dayOfYear - Math.floor((153 * shiftedMonth + 2) / 5) + 1;
    const month = shiftedMonth + (shiftedMonth < 10 ? 3 : -9);
    return { year: month <= 2 ? shiftedYear + 1 : shiftedYear, month, day };
};
export const addDays = (date, days) => fromEpochDay(toEpochDay(date) + assertInteger(days, 'days'));
export const differenceInDays = (from, to) => toEpochDay(to) - toEpochDay(from);
const DAYS_PER_WEEK = 7;
/** Epoch day zero, 1970-01-01, was a Thursday - the fourth day of an ISO week. */
const EPOCH_WEEKDAY_OFFSET = 3;
/** ISO 8601 numbering: 1 is Monday, 7 is Sunday. */
export const dayOfWeek = (date) => {
    const shifted = toEpochDay(date) + EPOCH_WEEKDAY_OFFSET;
    return (((shifted % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK) + 1;
};
export const compareCivilDates = (left, right) => {
    if (left.year !== right.year) {
        return left.year < right.year ? -1 : 1;
    }
    if (left.month !== right.month) {
        return left.month < right.month ? -1 : 1;
    }
    if (left.day !== right.day) {
        return left.day < right.day ? -1 : 1;
    }
    return 0;
};
export const isSameCivilDate = (left, right) => compareCivilDates(left, right) === 0;
export const isCivilDateBefore = (left, right) => compareCivilDates(left, right) < 0;
export const isCivilDateAfter = (left, right) => compareCivilDates(left, right) > 0;
export const formatCivilDate = (date) => {
    const year = String(assertInteger(date.year, 'year')).padStart(4, '0');
    const month = String(assertInteger(date.month, 'month')).padStart(2, '0');
    const day = String(assertInteger(date.day, 'day')).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
export const parseCivilDate = (value) => {
    const matched = ISO_DATE_PATTERN.exec(value);
    if (matched === null) {
        throw new RangeError(`Expected a YYYY-MM-DD date, received "${value}"`);
    }
    const [, year, month, day] = matched;
    if (year === undefined || month === undefined || day === undefined) {
        throw new RangeError(`Expected a YYYY-MM-DD date, received "${value}"`);
    }
    const date = {
        year: Number.parseInt(year, 10),
        month: Number.parseInt(month, 10),
        day: Number.parseInt(day, 10),
    };
    const lastDayOfMonth = daysInMonth(date.year, date.month);
    if (date.day < 1 || date.day > lastDayOfMonth) {
        throw new RangeError(`Day out of range for that month: "${value}"`);
    }
    return date;
};
const readNumericPart = (parts, type) => {
    const part = parts.find((candidate) => candidate.type === type);
    if (part === undefined) {
        throw new Error(`Intl.DateTimeFormat returned no "${type}" part`);
    }
    const value = Number.parseInt(part.value, 10);
    if (!Number.isInteger(value)) {
        throw new Error(`Intl.DateTimeFormat returned a non-numeric "${type}" part: "${part.value}"`);
    }
    return value;
};
/**
 * The only place in the domain that knows time zones exist. Everything downstream works on
 * civil dates, so `now` must always be supplied by the caller — the domain never reads the clock.
 */
export const toCivilDate = (instant, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        calendar: 'gregory',
        numberingSystem: 'latn',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    return {
        year: readNumericPart(parts, 'year'),
        month: readNumericPart(parts, 'month'),
        day: readNumericPart(parts, 'day'),
    };
};
//# sourceMappingURL=civil-date.js.map